/* eslint-disable @typescript-eslint/naming-convention */
// internal-deps
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { GroupOrderAnalyticsPayload } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { CLICK_ACTIONS, SCREEN_NAME, IMPRESSION_ACTIONS } from "../constants";
import { DEAL_RUSH_GROUP_TAG_ID } from "@cart-client/constants/index";

export const clickAddMoreItemsCta = (clickEvent: AnalyticsContextValue["clickEvent"], context: string): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_ADD_MORE_ITEMS,
        context,
    });
};

export const clickItemAdd = (
    item: { itemId: string; itemPosition: number; itemType: string },
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_ITEM_ADD,
        label: item.itemId,
        value: item.itemPosition,
        context: item.itemType,
    });
};

export const clickItemIncrement = (
    item: { itemId: string; itemQuantity: number; itemType: string; itemGroupTagId?: string | null },
    clickEvent: AnalyticsContextValue["clickEvent"],
    groupOrderMeta?: GroupOrderAnalyticsPayload,
): void => {
    const screenName = !isEmpty(groupOrderMeta?.isGroupCart) ? SCREEN_NAME.GROUP_CART : SCREEN_NAME.CHECKOUT;
    const isDealRush = item.itemGroupTagId === DEAL_RUSH_GROUP_TAG_ID;
    const extraParamsData = {
        ...groupOrderMeta,
        grp_cart_id: groupOrderMeta?.groupCartId,
        ...(isDealRush && { offer_applied: DEAL_RUSH_GROUP_TAG_ID }),
    };
    clickEvent({
        category: screenName,
        action: CLICK_ACTIONS.CLICK_ITEM_INCREMENT,
        label: item.itemId,
        value: item.itemQuantity,
        context: item.itemType,
        extraParams: JSON.stringify(extraParamsData),
    });
};

export const clickItemDecrement = (
    item: { itemId: string; itemQuantity: number; itemType: string; itemGroupTagId?: string | null },
    clickEvent: AnalyticsContextValue["clickEvent"],
    groupOrderMeta?: GroupOrderAnalyticsPayload,
): void => {
    const screenName = !isEmpty(groupOrderMeta?.isGroupCart) ? SCREEN_NAME.GROUP_CART : SCREEN_NAME.CHECKOUT;
    const isDealRush = item.itemGroupTagId === DEAL_RUSH_GROUP_TAG_ID;
    const extraParamsData = {
        ...groupOrderMeta,
        grp_cart_id: groupOrderMeta?.groupCartId,
        ...(isDealRush && { offer_applied: DEAL_RUSH_GROUP_TAG_ID }),
    };
    clickEvent({
        category: screenName,
        action: CLICK_ACTIONS.CLICK_ITEM_DECREMENT,
        label: item.itemId,
        value: item.itemQuantity,
        context: item.itemType,
        extraParams: JSON.stringify(extraParamsData),
    });
};

export const impressionNormalCustimsation = (
    itemId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_NORMAL_CUSTOMIZATION,
        label: itemId,
    });
};

export const impressionProgressiveCustimsation = (
    itemId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_PROGRESSIVE_CUSTOMIZATION,
        label: itemId,
    });
};

export const clickCustomiseUpdateItem = (itemId: string, clickEvent: AnalyticsContextValue["clickEvent"]): void => {
    clickEvent({
        category: SCREEN_NAME.CUSTOMIZATION,
        action: CLICK_ACTIONS.CLICK_CUSTOMISATION_UPDATE_ITEM,
        label: itemId,
    });
};

export const clickRemoveSingleOOSItem = (
    menuItem: { itemId: string; price: number },
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId?: number,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_REMOVE_SINGLE_OOS_ITEM,
        label: menuItem.itemId,
        value: menuItem.price,
        context: cartId !== undefined ? JSON.stringify({ cartId }) : undefined,
    });
};

export const impressionCrossSellingItem = (
    item: { id: string; position: number },
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CROSS_SELLING_ITEM,
        label: item.id,
        value: item.position,
    });
};

export const impressionCrossSelling = (
    suggestionCount: number,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CHECKOUT_CROSS_SELLING,
        value: suggestionCount,
    });
};

export const impressionCartItemImage = (
    context: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ITEM_IMAGE,
        context,
    });
};

export const clickEmptyCartRedirect = (
    params: { cartId?: string },
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_BROWSE_RESTAURANTS,
        context: JSON.stringify({ cartId: params.cartId }),
    });
};

export const impressionEmptyCartRedirect = (
    params: { cartId?: string },
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_EMPTY_CART_REDIRECT,
        context: JSON.stringify({ cartId: params.cartId }),
    });
};

export const impressionItemListHeader = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    isDealRush = false,
): void => {
    const extraParamsData = {
        ...(isDealRush && { offer_applied: DEAL_RUSH_GROUP_TAG_ID }),
    };
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ITEM_LIST_SECTION_HEADER,
        extraParams: Object.keys(extraParamsData).length > 0 ? JSON.stringify(extraParamsData) : undefined,
    });
};
